import isEmpty from 'lodash/isEmpty';
import {TRACK_EVENTS_MAP} from '@teladoc/analytics';
import AmplitudeClient from '@livongo/utilities/system/amplitude';
import {EVENT_PROPS, CATEGORY_PROPS} from '@/config';

// Image mapping constants
const IMAGE_MAPPINGS = {
    ACTIVITY: 'DigitalPhysicalTherapist2',
    DEVICE: 'ActivateDevices',
    NUTRITION: 'Nutrition',
    HEALTH_CARE: 'ConditionManagement',
    MEDICATION: 'Medications',
    SLEEP: 'Sleep',
    SOCIAL_SUPPORT_COACH: 'SocialSupport',
    STRESS_MENTAL_HEALTH: 'MentalHealth',
    SUBSTANCE_USE: 'SubstanceUse',
    LESSON: 'Lesson',
};

const DEVICE_SPECIFIC_MAPPINGS = {
    MANAGE_BLOOD_GLUCOSE: 'DeviceCheckBloodSugar',
    MANAGE_BLOOD_PRESSURE: 'DeviceCheckBloodPressure',
    MANAGE_WEIGHT: 'DeviceWeighIn',
    START_TRACKING_BODY_COMPOSITION: 'WeightManagementWM2000',
    SYNC_YOUR_CGM: 'Cgm',
};

const DEFAULT_IMAGES = {
    DEVICE: 'ActivateDevices',
    GENERIC: 'GenericTask',
};

const FREQUENCY_MAPPINGS = {
    'One time': 'Once',
};

const NextStepsUtils = {
    // Export the frequency mappings
    FREQUENCY_MAPPINGS,

    /**
     * Maps image ID and code combinations to spot illustration names
     * @param {string} imageId - The primary image category
     * @param {string} code - The specific action/condition code
     * @returns {string} The corresponding illustration name
     */
    getSpotIllustrationName(imageId, code) {
        // Input validation
        if (!imageId || typeof imageId !== 'string') {
            return DEFAULT_IMAGES.GENERIC;
        }

        // Handle special cases first
        if (code === 'SET_GOALS' && imageId === 'HEALTH_CARE') {
            return 'Goals';
        }

        // Handle device-specific mappings
        if (imageId === 'DEVICE') {
            return DEVICE_SPECIFIC_MAPPINGS[code] || DEFAULT_IMAGES.DEVICE;
        }

        // Handle general image mappings
        return IMAGE_MAPPINGS[imageId] || DEFAULT_IMAGES.GENERIC;
    },

    getTaskIdFromUrl({taskPath = '', myCustomPath = null}) {
        if (isEmpty(taskPath)) {
            return '';
        }

        // Extracts the task ID from the current URL
        const {pathname} = document.location;
        // Use provided path or fallback to current pathname
        const pathToUse = myCustomPath || pathname;

        if (typeof pathToUse !== 'string' || isEmpty(pathToUse.trim())) {
            return '';
        }

        // Split pathname and find task-detail segment
        const segments = pathToUse.split('/').filter(Boolean);
        const taskDetailIndex = segments.indexOf(taskPath);

        // Return the next segment after 'task-detail' if it exists and is numeric
        if (taskDetailIndex !== -1 && segments[taskDetailIndex + 1]) {
            const taskId = segments[taskDetailIndex + 1];

            return Number.isInteger(Number(taskId)) && Number(taskId) > 0
                ? taskId
                : '';
        }

        return '';
    },

    truncateBehaviorLabel(label) {
        // Remove "Behavior:" prefix if it exists
        return label?.replace(/^Behavior:\s*/i, '');
    },

    getProductLine(label) {
        if (label === 'SELECT_PHARMACY' || label === 'ADD_DEPENDENT') {
            return 'Global';
        } else if (label === 'ENROLL_IN_CONDITION_MANAGEMENT') {
            return 'Chronic Condition Management';
        } else {
            return label;
        }
    },

    // `errorType` names the area the error surfaced in, from
    // ERROR_TRACKING_COMPONENTS.
    setAmplitudeErrorTracking(screenName, {headline, message, errorType} = {}) {
        return AmplitudeClient.track(TRACK_EVENTS_MAP.errorView, {
            // eslint-disable-next-line camelcase
            error_type: errorType,
            // eslint-disable-next-line camelcase
            error_headline: headline || 'Something went wrong',
            // eslint-disable-next-line camelcase
            error_message:
                message ||
                "Try refreshing the page. If that doesn't help, close and reopen your browser.",
            // eslint-disable-next-line camelcase
            screen_name: screenName,
            // eslint-disable-next-line camelcase
            product_line: EVENT_PROPS.Global,
            category: CATEGORY_PROPS.Tasks,
        });
    },
};

export default NextStepsUtils;
